import { LEGAL_DOCUMENT_TYPES } from "@/components/project/OnboardingUploads";

export { LEGAL_DOCUMENT_TYPES };

export function legalDocumentLabel(docId) {
  return LEGAL_DOCUMENT_TYPES.find((item) => item.id === docId)?.label || docId.replace(/_/g, " ");
}

export function activeLegalDocuments(documents = {}) {
  return LEGAL_DOCUMENT_TYPES
    .map((type) => {
      const doc = documents?.[type.id];
      if (!doc?.url) return null;
      return {
        id: type.id,
        label: type.label,
        url: doc.url,
        original_filename: doc.original_filename || "",
        content_type: doc.content_type || "",
        uploaded_at: doc.uploaded_at || null,
        content_text: sanitizeLegalContentText(doc.content_text || ""),
        extraction_method: doc.extraction_method || "",
      };
    })
    .filter(Boolean);
}

export function sanitizeLegalContentText(text) {
  const raw = String(text || "");
  if (!raw.trim()) return "";
  // Strip WordprocessingML / OOXML that was incorrectly stored as content.
  if (/<w:(?:t|p|tbl|tr|tc|r|tblPr|document|body)\b/i.test(raw) || /<\/w:(?:t|p|tbl|tr|tc|r)>/i.test(raw)) {
    return raw
      .replace(/<\/w:p>/gi, "\n")
      .replace(/<\/w:tr>/gi, "\n")
      .replace(/<w:tab\b[^>]*\/>/gi, "\t")
      .replace(/<w:br\b[^>]*\/>/gi, "\n")
      .replace(/<w:t\b[^>]*>([\s\S]*?)<\/w:t>/gi, "$1")
      .replace(/<[^>]+>/g, "")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, "\"")
      .replace(/&apos;/g, "'")
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  return raw.trim();
}

export function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Pull a "Last updated" line out of extracted text when present. */
export function extractLegalUpdatedLabel(text) {
  const match = String(text || "").match(/^\s*Last updated\s*:\s*(.+)$/im);
  return match ? match[1].trim() : null;
}

/**
 * Convert plain extracted legal text into safe HTML blocks
 * (headings, paragraphs, lists) for a document-style page.
 */
export function formatLegalHtml(text, { title } = {}) {
  const sanitized = sanitizeLegalContentText(text);
  if (!sanitized) return "";

  const lines = sanitized.split(/\n/);
  const blocks = [];
  let listItems = [];
  let prevWasBlank = true;
  const titleNorm = String(title || "").trim().toLowerCase();

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(`<ul>${listItems.map((item) => `<li>${item}</li>`).join("")}</ul>`);
    listItems = [];
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const rawLine = lines[lineIndex];
    const trimmed = rawLine.trim();
    if (!trimmed) {
      flushList();
      prevWasBlank = true;
      continue;
    }

    // Skip meta / duplicate page title lines — shown in the page chrome instead.
    if (/^last updated\s*:/i.test(trimmed)) { prevWasBlank = false; continue; }
    if (/^effective (as of|date)\s*:/i.test(trimmed)) { prevWasBlank = false; continue; }
    if (titleNorm && trimmed.toLowerCase() === titleNorm) { prevWasBlank = false; continue; }
    if (
      titleNorm
      && new RegExp(`^${titleNorm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[—\\-–:].+$`, "i").test(trimmed)
      && trimmed.length < 120
    ) {
      prevWasBlank = false;
      continue;
    }

    const bullet = trimmed.match(/^[-•*]\s+(.+)$/);
    if (bullet) {
      listItems.push(escapeHtml(bullet[1]));
      prevWasBlank = false;
      continue;
    }
    // PDF extracts sometimes omit the space after the bullet glyph.
    const tightBullet = trimmed.match(/^[-•*](.+)$/);
    if (tightBullet && !/^\d/.test(tightBullet[1])) {
      listItems.push(escapeHtml(tightBullet[1].trim()));
      prevWasBlank = false;
      continue;
    }

    flushList();

    // Markdown table rows
    if (trimmed.startsWith("|") && trimmed.includes("|", 1)) {
      const tableRows = [];
      while (lineIndex < lines.length) {
        const row = lines[lineIndex].trim();
        if (!row.startsWith("|")) break;
        tableRows.push(row);
        lineIndex += 1;
      }
      lineIndex -= 1;
      // Filter separator rows like |---|---|
      const bodyRows = tableRows
        .map((row) => row.split("|").slice(1, -1).map((cell) => cell.trim()))
        .filter((cells) => cells.some((cell) => cell) && !cells.every((cell) => /^:?-{3,}:?$/.test(cell)));
      if (bodyRows.length) {
        const [header, ...rest] = bodyRows;
        const thead = `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>`;
        const tbody = rest.map((cells) => `<tr>${cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`).join("");
        blocks.push(`<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`);
      }
      prevWasBlank = false;
      continue;
    }

    const numbered = trimmed.match(/^(\d+(?:\.\d+){0,3})\.?\s+(.+)$/);
    if (numbered) {
      const depth = numbered[1].split(".").length;
      const rest = numbered[2].trim();
      const looksLikeHeading = trimmed.length <= 110 || !/[.!?]$/.test(rest) || rest.split(/\s+/).length <= 12;
      if (looksLikeHeading) {
        const tag = depth <= 1 ? "h2" : depth === 2 ? "h3" : "h4";
        blocks.push(`<${tag}>${escapeHtml(trimmed)}</${tag}>`);
        prevWasBlank = false;
        continue;
      }
    }

    const isAllCaps =
      trimmed.length <= 80
      && /[A-Za-zÄÖÜäöüß]/.test(trimmed)
      && trimmed === trimmed.toUpperCase()
      && trimmed.split(/\s+/).length <= 12;
    if (isAllCaps) {
      blocks.push(`<h2>${escapeHtml(trimmed)}</h2>`);
      prevWasBlank = false;
      continue;
    }

    // Section headings (Impressum / policy style): short lead-in before a longer body line.
    const looksLikeEntityOrAddress =
      /\d/.test(trimmed)
      || /@/.test(trimmed)
      || /^https?:\/\//i.test(trimmed)
      || /\b(UG|GmbH|e\.K\.|Ltd|LLC|Inc|haftungsbeschränkt|HRB|HRA|VAT)\b/i.test(trimmed);
    let nextNonEmpty = "";
    for (let lookAhead = lineIndex + 1; lookAhead < lines.length; lookAhead += 1) {
      const candidate = lines[lookAhead].trim();
      if (candidate) {
        nextNonEmpty = candidate;
        break;
      }
    }
    const knownHeading = /^(contact details|contact|eu dispute resolution|liability for content|liability for links|copyright notice|copyright|scope of this impressum|responsible for content|note)$/i.test(
      trimmed.replace(/:$/, "")
    );
    const words = trimmed.split(/\s+/);
    const significant = words.filter((word) => !/^(for|of|and|the|to|a|an|or|in|on|by|pursuant|according|this)$/i.test(word));
    const capitalized = significant.filter((word) => /^[A-ZÄÖÜ]/.test(word));
    const isStructuralHeading =
      prevWasBlank
      && !looksLikeEntityOrAddress
      && /^[A-ZÄÖÜ]/.test(trimmed)
      && trimmed.length >= 8
      && trimmed.length <= 70
      && !/[.!?]$/.test(trimmed)
      && words.length <= 10
      && nextNonEmpty
      && (
        knownHeading
        || nextNonEmpty.length >= 50
        || (capitalized.length >= Math.ceil(significant.length * 0.6) && nextNonEmpty.length >= 24)
      );

    if (isStructuralHeading) {
      blocks.push(`<h2>${escapeHtml(trimmed)}</h2>`);
      prevWasBlank = false;
      continue;
    }

    blocks.push(`<p>${escapeHtml(trimmed)}</p>`);
    prevWasBlank = false;
  }

  flushList();
  return blocks.join("");
}

export function formatLegalUpdatedDate(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function formatBusinessAddress(address = {}) {
  if (!address || address.show_on_storefront === false) return "";
  return [address.line1, address.line2, address.city, address.postcode, address.country]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

const DACH_COUNTRY_ALIASES = new Set([
  "germany",
  "deutschland",
  "de",
  "austria",
  "österreich",
  "osterreich",
  "at",
  "switzerland",
  "schweiz",
  "suisse",
  "svizzera",
  "ch",
]);

/** Germany, Austria, and Switzerland require an Impressum. */
export function isDachCountry(country) {
  const value = String(country || "").trim().toLowerCase();
  if (!value) return false;
  if (DACH_COUNTRY_ALIASES.has(value)) return true;
  return /\b(germany|deutschland|austria|österreich|osterreich|switzerland|schweiz)\b/i.test(value);
}

export function hasLegalDocument(documents, docId) {
  return Boolean(documents?.[docId]?.url);
}

const SOFT_LEGAL_REQUIREMENTS = [
  { id: "privacy_policy", label: "Privacy Policy" },
  { id: "terms_and_conditions", label: "Terms & Conditions" },
  { id: "cookie_policy", label: "Cookie Policy" },
];

/** Soft gaps (recommended) + hard Impressum gap for DACH countries. */
export function getLegalComplianceGaps({ legal_documents: documents = {}, country } = {}) {
  const softMissing = SOFT_LEGAL_REQUIREMENTS.filter((item) => !hasLegalDocument(documents, item.id));
  const impressumRequired = isDachCountry(country);
  const impressumMissing = impressumRequired && !hasLegalDocument(documents, "impressum");
  return {
    softMissing,
    softMissingLabels: softMissing.map((item) => item.label),
    impressumRequired,
    impressumMissing,
    hasAnyGap: impressumMissing || softMissing.length > 0,
  };
}
